<template>
  <LcModal
    v-bind="{ isVisible }"
    background-class="cms-fullscreen__modal--background"
    :close-triggers="['click-outside', 'escape']"
    :tracking="{ topicKey }"
    @close="handleClose"
  >
    <template #modal="{ setRef }">
      <LcCard
        :ref="setRef"
        body-class="flex flex-col h-full p-none"
        class="lc-modal__card lc-modal__card--large"
        role="dialog"
      >
        <header class="flex items-center justify-between p-100 headline-h5-headline">
          <LcSkeleton v-if="pending" height="1.5rem" radius="none" />
          <h5 v-else class="headline-h5-headline">{{ title }}</h5>

          <LcButton icon="close" size="small" variant="tertiary" @click="handleClose" />
        </header>

        <section
          v-if="error"
          :class="[
            'flex',
            'items-center',
            'justify-center',
            {
              'h-screen': isMobile,
            },
          ]"
        >
          <Illustration name="error-notfound" />
        </section>

        <LcSkeleton v-else-if="pending" height="100vh" radius="none" />

        <CmsContent v-else class="cms-content flex flex-col h-full px-100" :data="pageData" />
      </LcCard>
    </template>
  </LcModal>
</template>

<script lang="ts" setup>
import type { ContentBlock } from '../../shared/@types/generated/cms/graphql-schema-types'

type Props = { pageId: string; topicKey: string }

const isContentBlock = (property: unknown): property is ContentBlock =>
  (property as ContentBlock).__typename === 'ContentBlock'

const { pageId } = defineProps<Props>()

const isVisible = defineModel('isVisible', { default: false, type: Boolean })

const { log } = useDatadog()
const { t } = useI18n()
const { isMobile } = useDevice()
const { data, error, pending } = await useContent().getPage(pageId)

const pageData = computed(
  () => data.value?.contentBlocksCollection?.items.filter(isContentBlock) ?? [],
)

const title = computed(() => {
  if (error.value) return t('common.sentences.somethingwentwrong')
  return data.value?.title || ''
})

const handleClose = () => {
  isVisible.value = false
}

watchImmediate(error, () => {
  if (!error.value) return
  log('error', {
    component: 'ModalCmsPageFullscreen.vue',
    error: error.value,
    message: `Failed to load CMS page with id: ${pageId}`,
  })
})
</script>

<style lang="scss">
.cms-fullscreen {
  &__card {
    overflow: hidden;
    background-color: token('color/background/secondary/secondary/bold');

    @include media-tablet {
      height: 100%;
      border-radius: 0;
    }
  }

  &__modal {
    &--background {
      padding: 4rem;

      @include media-tablet {
        padding: 0;
        background: none;
        backdrop-filter: none;
      }
    }
  }
}

.cms-content {
  overflow-y: auto;
}
</style>
